import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import useClickedOutsideElement from '../../hooks/useClickedOutsideElement';
import Icon, { IconType } from '../icon';
import { useIntl } from 'react-intl';
import translations from './button.i18n';
import * as css from './button.module.scss';

type HTMLButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

interface Props extends HTMLButtonProps {
  className?: string;
  testSelector?: string;
  disableOnClickBlur?: boolean;
}

interface PrivateButtonProps extends Props {
  variant: ButtonVariant;
}

// This is exportet only for use within splitbutton
export enum ButtonVariant {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
  Quaternary = 'quaternary',
  Quinary = 'quinary',
  ClearCloseRemove = 'clearCloseRemove',
}

// This is exportet only for use within splitbutton
// Don't use this directly other places
// Instead use Button.Primary, Button.Secondary and Button.Tertiary
export const NativeButton: React.FC<
  React.PropsWithChildren<PrivateButtonProps>
> = ({
  children,
  className,
  variant = ButtonVariant.Primary,
  testSelector,
  disableOnClickBlur,
  ...props
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const clickedOutside = useClickedOutsideElement(ref);

  const onMouseUp = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (disableOnClickBlur) {
      return;
    }

    if (props.onMouseUp) {
      props.onMouseUp(event);
    }

    if (ref.current) {
      ref.current.blur();
    }
  };

  useEffect(() => {
    if (!disableOnClickBlur && clickedOutside && ref.current) {
      ref.current.blur();
    }
  }, [clickedOutside, ref, disableOnClickBlur]);

  return (
    <button
      data-cy={testSelector}
      className={classnames(css.button, css[variant], className)}
      onMouseUp={onMouseUp}
      ref={ref}
      {...props}
    >
      {children}
    </button>
  );
};
const Primary: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  ...props
}) => (
  <NativeButton variant={ButtonVariant.Primary} {...props}>
    {children}
  </NativeButton>
);

const Secondary: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  ...props
}) => (
  <NativeButton variant={ButtonVariant.Secondary} {...props}>
    {children}
  </NativeButton>
);

const Tertiary: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  ...props
}) => (
  <NativeButton variant={ButtonVariant.Tertiary} {...props}>
    {children}
  </NativeButton>
);

const Quaternary: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  ...props
}) => (
  <NativeButton variant={ButtonVariant.Quaternary} {...props}>
    {children}
  </NativeButton>
);

const Quinary: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  ...props
}) => (
  <NativeButton variant={ButtonVariant.Quinary} {...props}>
    {children}
  </NativeButton>
);

const Add: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  ...props
}) => (
  <NativeButton
    variant={ButtonVariant.Secondary}
    className={classnames(css.addBtn, props.className)}
    {...props}
  >
    {children}
    <Icon
      className={css.icon}
      type={IconType.AddCircleRounded}
      title={props.name}
    />
  </NativeButton>
);

enum TranslationVariant {
  Clear,
  Close,
  Remove,
}

type NameTranslations = {
  clear: string;
  close: string;
  remove: string;
};

const getDefaultName = (
  variant: TranslationVariant,
  defaultNames: NameTranslations,
): string => {
  switch (variant) {
    case TranslationVariant.Clear:
      return defaultNames.clear;
    case TranslationVariant.Close:
      return defaultNames.close;
    case TranslationVariant.Remove:
      return defaultNames.remove;
  }
};

type ClearCloseRemoveType = Props & {
  translationVariant: TranslationVariant;
};

const ClearCloseRemove: React.FC<
  React.PropsWithChildren<ClearCloseRemoveType>
> = ({ children, translationVariant, name, ...props }) => {
  const { formatMessage } = useIntl();
  const defaultNames = {
    clear: formatMessage({ id: translations.clear.id }),
    close: formatMessage({ id: translations.close.id }),
    remove: formatMessage({ id: translations.remove.id }),
  };

  const newName = name || getDefaultName(translationVariant, defaultNames);

  return (
    <NativeButton
      variant={ButtonVariant.ClearCloseRemove}
      name={newName}
      aria-label={newName}
      {...props}
    >
      <Icon type={IconType.Clear} title={newName} />
    </NativeButton>
  );
};

const Clear: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  name,
  ...props
}) => (
  <ClearCloseRemove translationVariant={TranslationVariant.Clear} {...props}>
    {children}
  </ClearCloseRemove>
);

const Close: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  name,
  ...props
}) => (
  <ClearCloseRemove translationVariant={TranslationVariant.Close} {...props}>
    {children}
  </ClearCloseRemove>
);

const Remove: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  name,
  ...props
}) => (
  <ClearCloseRemove translationVariant={TranslationVariant.Remove} {...props}>
    {children}
  </ClearCloseRemove>
);

const Buttons = {
  Primary,
  Secondary,
  Tertiary,
  Quaternary,
  Quinary,
  Clear,
  Close,
  Remove,
  Add,
};

export default Buttons;
